import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ApiHelper } from './api.helper';
import { HcService } from '@project/services';
import { monitorLogger } from '../../project/monitor/monitor-logger';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected constructor(protected http: HttpClient, private hcService: HcService) {}

  public get<T = any>(
    url: string,
    queryParams: Record<string, any> = {},
    responseType: 'json' | 'text' = 'json',
  ): Observable<T> {
    return this.request('GET', url, null, queryParams, responseType).pipe(map((response) => response as T));
  }

  public getBlob(url: string, queryParams: Record<string, any> = {}, raw: boolean = false): Observable<Blob> {
    return this.request('GET', url, null, queryParams, 'blob', raw).pipe(map((response) => response as Blob));
  }

  public post<T = any>(
    url: string,
    postData: Record<string, any> | string | null = {},
    responseType: 'json' | 'text' = 'json',
    customHeaders: Record<string, string> = null,
  ): Observable<T> {
    const payload = typeof postData === 'string' ? JSON.stringify(postData) : postData;

    return this.request('POST', url, payload, {}, responseType, false, customHeaders).pipe(
      map((response) => response as T),
    );
  }

  public postRaw<T = any>(url: string, postData: Record<string, any> | string = {}): Observable<T> {
    return this.request('POST', url, postData, {}, 'json', true).pipe(map((response) => response as T));
  }

  public patch<T = any>(
    url: string,
    postData: Record<string, any> | string = {},
    responseType: 'json' | 'text' = 'json',
  ): Observable<T> {
    const payload = typeof postData === 'string' ? postData : JSON.stringify(postData);
    return this.request('PATCH', url, payload, {}, responseType).pipe(map((response) => response as T));
  }

  public put<T = any>(
    url: string,
    postData: Record<string, any> | string = {},
    responseType: 'json' | 'text' = 'json',
  ): Observable<T> {
    const payload = typeof postData === 'string' ? JSON.stringify(postData) : postData;

    return this.request('PUT', url, payload, {}, responseType).pipe(map((response) => response as T));
  }

  public postWithForm<T = any>(url: string, postData: FormData): Observable<T> {
    return this.http.post<T>(url, postData).pipe(map((response) => response as T));
  }

  public delete<T = void>(url: string, body?: any, responseType: 'json' | 'text' = 'json'): Observable<T> {
    return this.request('DELETE', url, body, {}, responseType).pipe(map((response) => response as T));
  }

  /**
   * Generic request method
   * Used by other methods to perform HTTP requests
   *
   * @param method HTTP method
   * @param url URL
   * @param body Request body
   * @param params Query parameters
   * @param responseTypeInput Response type
   * @param raw Whether to use headers and credentials or not. If true, no headers and no credentials are used.
   * @param customHeaders Custom headers to be used in the request
   * @returns Observable with the response
   */
  protected request(
    method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH' = 'GET',
    url: string,
    body: any = null,
    params: Record<string, any> = {},
    responseTypeInput: 'json' | 'text' | 'blob' = 'json',
    raw: boolean = false,
    customHeaders: Record<string, string> = null,
  ): Observable<unknown> {
    const hcConfig = this.hcService.getHcConfig();
    const clientDomain = hcConfig.config.clientDomainHeader
      ? { 'PTM-Client-Domain': hcConfig.config.clientDomainHeader }
      : {};

    const headers = raw
      ? undefined
      : new HttpHeaders({
          'Content-Type': 'application/json',
          ...clientDomain,
          ...customHeaders,
        });

    const reqParams = params ? ApiHelper.convertObjectToHttpParams(params) : null;

    const request = new HttpRequest(method, url, body, {
      headers,
      params: reqParams,
      responseType: responseTypeInput,
      withCredentials: !raw,
    });

    monitorLogger.http.request(method, url, body);

    return this.http.request(request).pipe(
      filter((r) => {
        return r instanceof HttpResponse;
      }),
      map((response: HttpResponse<any>) => {
        monitorLogger.http.response(method, response.status, response.url, response.body);
        return response.body;
      }),
    );
  }
}
